import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { authApi } from '../services/api';
import { isCrazyGames } from '../services/portal';
import { promptCrazyGamesLogin } from '../services/crazygamesAuth';
import { PlayerRole } from '../types';

interface AuthConfig {
  devLoginEnabled: boolean;
  allowedDevRoles: string[];
}

// Labels for the (dev-only) role dropdown; shown only when more than one role is
// allowed. On the public build a single role (User) is configured, so the
// dropdown is hidden and players just pick a username.
const ROLE_LABELS: Record<string, string> = {
  User: 'User — regular player',
  Moderator: 'Moderator — moderation access',
  Admin: 'Admin — full access',
};

export default function Home() {
  const { isAuthenticated, devLogin } = useAuthStore();
  const navigate = useNavigate();

  // Which login options the server allows. null = still loading.
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole]         = useState<string>(PlayerRole.User);

  useEffect(() => {
    if (isAuthenticated) navigate('/game');
  }, [isAuthenticated, navigate]);

  // Load which login options are enabled. Fail safe to "disabled".
  useEffect(() => {
    let cancelled = false;
    authApi.getConfig()
      .then(res => {
        if (cancelled) return;
        const roles: string[] = Array.isArray(res.data.allowedDevRoles) ? res.data.allowedDevRoles : [];
        setConfig({ devLoginEnabled: !!res.data.devLoginEnabled, allowedDevRoles: roles });
        if (roles.length) setRole(roles[0]);
      })
      .catch(() => {
        if (cancelled) return;
        setConfig({ devLoginEnabled: false, allowedDevRoles: [] });
      });
    return () => { cancelled = true; };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) { setError('Enter a username.'); return; }
    setError(''); setLoading(true);
    const result = await devLogin(username.trim(), role, password || undefined);
    setLoading(false);
    if (result.success) navigate('/game');
    else if (result.banned) navigate('/banned');
    else setError(result.message ?? 'Login failed.');
  };

  const input: React.CSSProperties = {
    width: '100%', padding: '0.6rem 0.75rem',
    background: '#1a1a2e', border: '1px solid #333', borderRadius: '4px',
    color: '#e0e0e0', fontFamily: 'monospace', fontSize: '0.9rem', boxSizing: 'border-box',
  };
  const label: React.CSSProperties = {
    display: 'block', marginBottom: '0.4rem', fontSize: '0.8rem',
    color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em',
  };

  const devEnabled = config?.devLoginEnabled ?? false;
  const roles      = config?.allowedDevRoles ?? [];
  const showRoles  = roles.length > 1;

  return (
    <div className="hero">
      <img
        src="/logo.svg"
        alt="ROOTKIN"
        width={84}
        height={84}
        style={{ display: 'block', margin: '0 auto 0.75rem' }}
      />
      <h1 style={{ letterSpacing: '0.22em' }}>ROOTKIN</h1>
      <p>Root the network, grow your kin. Hack systems, spread infections, complete missions, and climb the ranks.</p>

      <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
        <div className="card-header">
          <h2 className="card-title">Enter the Network</h2>
        </div>

        {isCrazyGames ? (
          // On CrazyGames external logins aren't allowed: the app auto-starts a session
          // (guest, or your CrazyGames account) — this view only shows briefly, and
          // offers the CrazyGames login for guests who want to save progress.
          <div style={{ textAlign: 'center', padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
            <p style={{ color: '#888', fontSize: '0.9rem', lineHeight: 1.5, margin: 0 }}>
              Starting your session… You can play right away as a guest.<br />
              Log in with CrazyGames to save your progress across devices.
            </p>
            <button className="primary" style={{ maxWidth: '320px' }} onClick={() => { void promptCrazyGamesLogin(); }}>
              Log in with CrazyGames
            </button>
          </div>
        ) : config === null ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '1rem 0' }}>Loading…</p>
        ) : loading ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '1rem 0' }}>Signing in…</p>
        ) : !devEnabled ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '1rem 0', lineHeight: 1.5 }}>
            Login is currently unavailable. Please try again later.
          </p>
        ) : (
          <>
            <p style={{ marginBottom: '1.25rem', color: '#888', fontSize: '0.85rem', lineHeight: 1.5 }}>
              Choose a username to enter — the same username always resumes the same account.<br />
              Set a password to protect it so no one else can take your account.
            </p>
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={label}>Username</label>
                <input
                  type="text" value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="e.g. NightHawk"
                  maxLength={64}
                  style={input} autoComplete="username" spellCheck={false}
                />
              </div>
              <div>
                <label style={label}>Password</label>
                <input
                  type="password" value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Set on first login · required after"
                  style={input} autoComplete="current-password"
                />
              </div>
              {showRoles && (
                <div>
                  <label style={label}>Role</label>
                  <select
                    value={role} onChange={e => setRole(e.target.value)}
                    style={{ ...input, cursor: 'pointer' }}
                  >
                    {roles.map(r => (
                      <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>
                    ))}
                  </select>
                </div>
              )}
              {error && <p style={{ color: '#ef4444', margin: 0, fontSize: '0.875rem' }}>{error}</p>}
              <button type="submit" className="primary" style={{ width: '100%' }}>
                Play
              </button>
            </form>
          </>
        )}
      </div>

      <div style={{ marginTop: '3rem', display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
        <div className="card">
          <div className="card-header"><h3 className="card-title">Hack Systems</h3></div>
          <p style={{ color: 'var(--text-muted)' }}>Use your toolkit to infiltrate secure networks and extract valuable data.</p>
        </div>
        <div className="card">
          <div className="card-header"><h3 className="card-title">Complete Missions</h3></div>
          <p style={{ color: 'var(--text-muted)' }}>Take on challenging missions to earn experience, credits, and reputation.</p>
        </div>
        <div className="card">
          <div className="card-header"><h3 className="card-title">Compete</h3></div>
          <p style={{ color: 'var(--text-muted)' }}>Climb the global leaderboard and prove you're the best hacker.</p>
        </div>
      </div>
    </div>
  );
}
